import { Button } from "@repo/ui/components/button"
import { Spinner } from "@repo/ui/components/spinner"
import { cn } from "@repo/ui/lib/utils"
import { Check, type LucideIcon } from "lucide-react"
import type { ReactNode } from "react"

/** A side navigation plus a content column, for pages made of sections. */
export function SettingsLayout({
  nav,
  children,
}: {
  nav: ReactNode
  children: ReactNode
}) {
  return (
    <div className="grid gap-6 lg:grid-cols-[13rem_minmax(0,1fr)] lg:gap-10">
      <aside className="lg:sticky lg:top-20 lg:self-start">{nav}</aside>
      <div className="flex max-w-3xl min-w-0 flex-col gap-8">{children}</div>
    </div>
  )
}

export interface SettingsNavItem<T extends string> {
  id: T
  label: string
  icon?: LucideIcon
}

/** Vertical on desktop, a scrolling row on small screens. */
export function SettingsNav<T extends string>({
  items,
  value,
  onSelect,
  href,
}: {
  items: SettingsNavItem<T>[]
  value?: T
  onSelect?: (id: T) => void
  /** Makes each item an anchor link to a section on the same page. */
  href?: (id: T) => string
}) {
  return (
    <nav
      aria-label="Sections"
      className="flex gap-1 overflow-x-auto lg:flex-col lg:overflow-visible"
    >
      {items.map((item) => {
        const className = cn(
          "flex shrink-0 items-center gap-2 rounded-lg px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground [&_svg]:size-4",
          value === item.id && "bg-muted font-medium text-foreground"
        )
        const content = (
          <>
            {item.icon && <item.icon />}
            {item.label}
          </>
        )

        return href ? (
          <a key={item.id} href={href(item.id)} className={className}>
            {content}
          </a>
        ) : (
          <button
            key={item.id}
            type="button"
            aria-current={value === item.id ? "page" : undefined}
            className={className}
            onClick={() => onSelect?.(item.id)}
          >
            {content}
          </button>
        )
      })}
    </nav>
  )
}

/** A titled block: heading and description on top, rows or a table below. */
export function SettingsSection({
  id,
  title,
  description,
  actions,
  children,
}: {
  id?: string
  title: string
  description?: ReactNode
  actions?: ReactNode
  children: ReactNode
}) {
  return (
    <section id={id} className="flex scroll-mt-24 flex-col gap-5">
      <div className="flex flex-col gap-3 border-b pb-5 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex max-w-xl flex-col gap-1">
          <h2 className="text-lg font-semibold">{title}</h2>
          {description && (
            <p className="text-sm text-muted-foreground">{description}</p>
          )}
        </div>
        {actions}
      </div>
      {children}
    </section>
  )
}

/** A label on the left and its control on the right; stacked on phones. */
export function SettingsRow({
  label,
  htmlFor,
  description,
  children,
}: {
  label: string
  htmlFor?: string
  description?: ReactNode
  children: ReactNode
}) {
  return (
    <div className="grid gap-2 border-b pb-5 last:border-b-0 last:pb-0 sm:grid-cols-[13rem_minmax(0,1fr)] sm:gap-6">
      <div className="flex flex-col gap-1">
        <label htmlFor={htmlFor} className="text-sm font-medium">
          {label}
        </label>
        {description && (
          <p className="text-xs text-muted-foreground">{description}</p>
        )}
      </div>
      <div className="flex min-w-0 flex-col gap-2">{children}</div>
    </div>
  )
}

/** Unsaved-changes note with Cancel and Save, shared by every settings form. */
export function SaveBar({
  dirty,
  saving,
  saved,
  error,
  onCancel,
  saveLabel = "Save",
}: {
  dirty: boolean
  saving: boolean
  saved: boolean
  error?: string | null
  onCancel: () => void
  saveLabel?: string
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 sm:justify-end">
      {error ? (
        <span role="alert" className="mr-1 text-sm text-destructive">
          {error}
        </span>
      ) : saved && !dirty ? (
        <span
          role="status"
          className="mr-1 flex items-center gap-1.5 text-sm text-emerald-600 dark:text-emerald-400"
        >
          <Check className="size-4" />
          Saved
        </span>
      ) : dirty ? (
        <span className="mr-1 text-sm text-muted-foreground">
          Unsaved changes
        </span>
      ) : null}
      <Button
        type="button"
        variant="outline"
        disabled={!dirty || saving}
        onClick={onCancel}
      >
        Cancel
      </Button>
      <Button type="submit" disabled={!dirty || saving}>
        {saving && <Spinner />}
        {saving ? "Saving" : saveLabel}
      </Button>
    </div>
  )
}
